// Smart timeline scroll hook with priority-based positioning
// CREATED: Intelligent scroll positioning that adapts to task context
// Priorities: 1) Overdue/urgent tasks, 2) Current task, 3) Upcoming task,
//            4) First task of day, 5) Fallback to 8am or current time
// FIXED: Scroll only triggers on explicit actions (date change, manual refresh)
// OPTIMIZED: Mobile UX with natural window.scrollTo() for native feel
// - Desktop: Uses container.scrollTo() for internal scroll within fixed viewport
// - Mobile: Uses window.scrollTo() for natural page scroll with smooth animation
// - Calculates timeline element offset from page top using getBoundingClientRect()
// - Ensures consistent scroll behavior across different viewport sizes

import { useEffect, useRef, RefObject } from 'react';
import { timeToMinutes, timeToPixelOffset } from '../lib/timeCalculations';

interface Task {
  id: string;
  startTime: string;
  duration: number;
  status?: string;
}

interface SmartScrollOptions {
  tasks: Task[];
  date: Date;
  scrollContainerRef: RefObject<HTMLDivElement>;
  scrollTriggerKey?: number;
  comfortOffsetMinutes?: number;
  isMobile?: boolean;
}

/**
 * Calculates the optimal scroll position based on task priorities
 * @param tasks - Array of tasks for the day
 * @param currentTime - Current time for comparison
 * @param comfortOffsetMinutes - Minutes to show above the target (default: 45)
 * @returns Optimal scroll position in pixels
 */
export function calculateOptimalScrollPosition(
  tasks: Task[],
  currentTime: Date,
  comfortOffsetMinutes: number = 45
): number {
  if (tasks.length === 0) {
    // No tasks: scroll to 8am or current time, whichever is more relevant
    const currentHour = currentTime.getHours();
    const targetHour = currentHour >= 6 && currentHour <= 20 ? currentHour : 8;
    const targetMinutes = targetHour * 60;
    return Math.max(0, timeToPixelOffset(`${targetHour.toString().padStart(2, '0')}:00`) - (comfortOffsetMinutes * 1));
  }

  const now = currentTime;
  const currentTimeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  const currentMinutes = timeToMinutes(currentTimeStr);

  // Priority 1: Overdue or urgent tasks (status includes 'late' or past their end time)
  const overdueTasks = tasks.filter(task => {
    const taskStart = timeToMinutes(task.startTime);
    const taskEnd = taskStart + task.duration;
    const isOverdue = taskEnd < currentMinutes && task.status !== 'completed';
    const isLate = task.status?.includes('late') || task.status?.includes('overdue');
    return isOverdue || isLate;
  });

  if (overdueTasks.length > 0) {
    const firstOverdue = overdueTasks.sort((a, b) =>
      timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
    )[0];
    const targetPosition = timeToPixelOffset(firstOverdue.startTime);
    return Math.max(0, targetPosition - (comfortOffsetMinutes * 1));
  }

  // Priority 2: Task currently in progress
  const currentTask = tasks.find(task => {
    const taskStart = timeToMinutes(task.startTime);
    const taskEnd = taskStart + task.duration;
    return currentMinutes >= taskStart && currentMinutes < taskEnd;
  });

  if (currentTask) {
    const targetPosition = timeToPixelOffset(currentTask.startTime);
    return Math.max(0, targetPosition - (comfortOffsetMinutes * 1));
  }

  // Priority 3: Next task within 2 hours
  const upcomingTasks = tasks
    .filter(task => {
      const taskStart = timeToMinutes(task.startTime);
      return taskStart > currentMinutes && taskStart <= currentMinutes + 120;
    })
    .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

  if (upcomingTasks.length > 0) {
    const nextTask = upcomingTasks[0];
    const targetPosition = timeToPixelOffset(nextTask.startTime);
    return Math.max(0, targetPosition - (comfortOffsetMinutes * 1));
  }

  // Priority 4: First task of the day
  const sortedTasks = [...tasks].sort((a, b) =>
    timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  );
  const firstTask = sortedTasks[0];
  const targetPosition = timeToPixelOffset(firstTask.startTime);
  return Math.max(0, targetPosition - (comfortOffsetMinutes * 1));
}

/**
 * Hook that automatically scrolls the timeline to the most relevant position
 * Handles both internal container scroll (desktop) and window scroll (mobile)
 */
export function useSmartTimelineScroll({
  tasks,
  date,
  scrollContainerRef,
  scrollTriggerKey,
  comfortOffsetMinutes = 45,
  isMobile = false
}: SmartScrollOptions) {
  // Use ref to store tasks without triggering effect on every task change
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  useEffect(() => {
    const currentTime = new Date();
    const scrollPosition = calculateOptimalScrollPosition(
      tasksRef.current,
      currentTime,
      comfortOffsetMinutes
    );

    // Mobile: use window scroll with smooth animation
    if (isMobile) {
      // Use the scrollContainerRef to find the timeline container
      // and calculate its offset from the top of the page
      if (scrollContainerRef.current) {
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const containerTop = containerRect.top + window.scrollY;

        // Add scroll position within the timeline to the container's top position
        const targetScrollPosition = containerTop + scrollPosition;

        // Use smooth scroll with slightly longer duration for premium feel
        window.scrollTo({
          top: targetScrollPosition,
          behavior: 'smooth'
        });
      }
    } else {
      // Desktop: use container scroll
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [date, scrollTriggerKey, comfortOffsetMinutes, isMobile, scrollContainerRef]);
}
